import { currentDisplayedStylesAtom, currentStyleIndexAtom } from "@/store/style/polarr-styles"
import { useTranslation } from "react-i18next"
import { processImageUrl, ImageFormat } from "@/utils/process-image-url"
import { Button } from "../ui/Button"
import { Pair, StylePreview, StylePreviewPanel } from "./StylePreviewPanel"
import { useUserQuery } from "@/api/users-api"
import { UserProfile } from "../ui/UserProfile"
import { CrossOriginImage } from "../ui/CrossOriginImage"
import { useAtom, useAtomValue, useSetAtom } from "jotai"
import { useMemo, useCallback, useEffect, useState, useRef, Suspense } from "react"
import { CompareDialog } from "./CompareDialog"
import { StyleNavigator } from "./StyleNavigator"
import {
	getLocalizedStyleDisplayName,
	getLocalizedStyleDescription,
} from "@/i18n/style-display-name"
import logoUrl from "@assets/logo.png?url"
import { UserStyleContextMenu } from "./UserStyleContextMenu"
import { StyleCover } from "./StyleCover"
import { StyleResponseDTO } from "@/api/dto/styles"
import { isPixiesetAuthEnabled } from "@/utils/measurement/feature-flag"
import { currentStyleAtom } from "@/store/style/style-state"
import { Icon } from "../icons/Icon"

interface StyleDetailProps {
	currentStyle: StyleResponseDTO
	isPolarrStyle: boolean
	onBack: () => void
	onUse: (style: StyleResponseDTO) => void
	onStyleChange?: (style: StyleResponseDTO, isPolarrStyle: boolean) => void
	isFullWidth?: boolean
	isLoadingUse?: boolean
}

export const StyleDetail = ({
	currentStyle,
	isPolarrStyle,
	onBack,
	onUse,
	onStyleChange,
	isFullWidth = false,
	isLoadingUse = false,
}: StyleDetailProps) => {
	const { t } = useTranslation("components")
	const userQuery = useUserQuery()
	const user = userQuery.data

	const [currentDisplayedStyles, setCurrentDisplayedStyles] = useAtom(currentDisplayedStylesAtom)
	const currentAppliedStyle = useAtomValue(currentStyleAtom)
	const setCurrentStyleIndex = useSetAtom(currentStyleIndexAtom)

	const [compareData, setCompareData] = useState<{
		photos: Pair<StylePreview>[]
		initialIndex: number
	} | null>(null)

	const LAYOUT_CONFIG = {
		containerPadding: isFullWidth ? "px-8" : "",
		stickyBgColor: isFullWidth ? "bg-base-900" : "bg-base-800",
		stickyHeaderPaddingTop: isFullWidth ? "pt-8" : "",
		headerMinHeight: isFullWidth ? "min-h-20" : "min-h-16",
	}

	const [showStickyButton, setShowStickyButton] = useState(false)
	const useButtonRef = useRef<HTMLDivElement>(null)
	const containerRef = useRef<HTMLDivElement>(null)

	// Intersection Observer for sticky title and button
	useEffect(() => {
		const container = containerRef.current
		const useButton = useButtonRef.current
		if (!container || !useButton) return

		// top-20 = 80px, top-16 = 64px
		const stickyOffset = isFullWidth ? 80 : 64
		const rootMargin = `-${stickyOffset}px 0px 0px 0px`

		const buttonObserver = new IntersectionObserver(
			(entries) => {
				const entry = entries[0]
				if (entry) {
					setShowStickyButton(!entry.isIntersecting)
				}
			},
			{
				root: container,
				rootMargin,
				threshold: 0,
			},
		)

		buttonObserver.observe(useButton)

		return () => {
			buttonObserver.disconnect()
		}
	}, [isFullWidth])

	const currentIndex = useMemo(() => {
		return currentDisplayedStyles.findIndex((style) => style.styleId === currentStyle.styleId)
	}, [currentDisplayedStyles, currentStyle])

	const displayedStyle = useMemo(() => {
		return currentDisplayedStyles[currentIndex]
	}, [currentIndex, currentDisplayedStyles])

	useEffect(() => {
		if (currentIndex >= 0) {
			setCurrentStyleIndex(currentIndex)
		}
	}, [currentIndex, setCurrentStyleIndex])

	useEffect(() => {
		if (containerRef.current) {
			containerRef.current.scrollTo({
				top: 0,
				behavior: "smooth",
			})
		}
	}, [currentStyle])

	const styleName = displayedStyle?.name ?? currentStyle.name
	const title = getLocalizedStyleDisplayName(styleName, t)
	const description = getLocalizedStyleDescription(styleName, currentStyle.description, t)
	const coverPhoto = displayedStyle?.coverImageUrl ?? currentStyle.coverImageUrl
	const author =
		currentStyle.author.name === "Pixieset" && !isPixiesetAuthEnabled()
			? "Polarr"
			: currentStyle.author.name

	const sampleBeforeAfterPhotos = useMemo(() => {
		if (isPolarrStyle) {
			const photos: Pair<StylePreview>[] =
				currentStyle["beforeAfterPhotos"]?.map(
					(pair: { afterImageUrl: string; beforeImageUrl: string }, index: number) => {
						const photoId = `polarr-style-photo-id-${index}`
						return {
							before: { photoId, url: pair.beforeImageUrl || null },
							after: { photoId, url: pair.afterImageUrl || null },
						}
					},
				) ?? []

			return photos
		} else {
			return []
		}
	}, [isPolarrStyle, currentStyle])

	const handlePrevious = useCallback(() => {
		if (currentDisplayedStyles.length > 1 && onStyleChange) {
			const previousIndex =
				currentIndex === 0 ? currentDisplayedStyles.length - 1 : currentIndex - 1
			const previousStyle = currentDisplayedStyles[previousIndex]
			if (previousStyle) {
				onStyleChange(previousStyle, isPolarrStyle)
			}
		}
	}, [currentIndex, currentDisplayedStyles, onStyleChange, isPolarrStyle])

	const handleNext = useCallback(() => {
		if (currentDisplayedStyles.length > 1 && onStyleChange) {
			const nextIndex =
				currentIndex === currentDisplayedStyles.length - 1 ? 0 : currentIndex + 1
			const nextStyle = currentDisplayedStyles[nextIndex]
			if (nextStyle) {
				onStyleChange(nextStyle, isPolarrStyle)
			}
		}
	}, [currentIndex, currentDisplayedStyles, onStyleChange, isPolarrStyle])

	const allStylesForNavigation = useMemo(() => {
		return currentDisplayedStyles.map((style) => ({
			name: style.name,
		}))
	}, [currentDisplayedStyles])

	const authorProfile = useMemo(() => {
		if (currentStyle.author.profilePictureUrl) {
			return (
				<CrossOriginImage
					className="flex size-7 items-center justify-center rounded-full border border-base-600 dark:border-transparent"
					src={currentStyle.author.profilePictureUrl ?? ""}
				/>
			)
		} else {
			if (isPolarrStyle) {
				return (
					<div className="flex size-7 items-center justify-center rounded-full">
						<img className="size-4" src={logoUrl} alt={t("styleDetail.altLogo")} />
					</div>
				)
			} else if (user) {
				return <UserProfile size={"xsmall"} user={user} />
			}
		}
	}, [isPolarrStyle, user, currentStyle.author.profilePictureUrl])

	const handlePreviewItemClick = (pairs: Pair<StylePreview>[], index: number) => {
		const photos = pairs
		if (!photos || photos.length === 0) {
			console.warn("No before/after photos available for comparison")
			return
		}

		setCompareData({
			photos,
			initialIndex: index,
		})
	}

	const previewPanel = () => {
		return (
			<Suspense>
				<StylePreviewPanel
					key={currentStyle.styleId}
					isPolarrStyle={isPolarrStyle}
					onPreviewItemClick={handlePreviewItemClick}
					sampleBeforeAfterPhotos={sampleBeforeAfterPhotos}
					isFullWidth={isFullWidth}
				/>
			</Suspense>
		)
	}

	const header = (
		<div
			className={`${LAYOUT_CONFIG.headerMinHeight} sticky -top-px z-[1] flex items-center justify-between ${LAYOUT_CONFIG.stickyBgColor} pb-6 ${LAYOUT_CONFIG.stickyHeaderPaddingTop}`}
		>
			<Button
				trackingLabel="go_back"
				aria-label={t("styleDetail.ariaGoBack")}
				variant="text"
				size="custom"
				onClick={onBack}
				className="text-base-400 hover:text-base-50"
			>
				<Icon name="arrow_back" size={20} />
			</Button>

			<div className="flex items-center space-x-4">
				{showStickyButton && (
					<Button
						onClick={() => {
							onUse(currentStyle)
						}}
						trackingValue={title}
						trackingLabel="use_ai_style"
						aria-label={t("styleDetail.ariaApplyAiStyle")}
						variant="primary"
						className="max-w-[320px] px-8 py-4 transition-all duration-300 ease-in-out"
						disabled={isLoadingUse}
					>
						<span className="truncate">
							{isLoadingUse
								? t("styleDetail.loadingAiStyle")
								: t("styleDetail.useStyleTitle", { title })}
						</span>
					</Button>
				)}

				{currentDisplayedStyles.length > 1 && currentIndex >= 0 && (
					<StyleNavigator
						currentIndex={currentIndex}
						totalCount={currentDisplayedStyles.length}
						onPrevious={handlePrevious}
						onNext={handleNext}
						allStyles={allStylesForNavigation}
					/>
				)}
			</div>
		</div>
	)

	const styleDescription = useMemo(() => {
		if (isPolarrStyle) {
			return (
				<p className="text-sm font-normal text-base-100" style={{ lineHeight: "23px" }}>
					{description}
				</p>
			)
		} else {
			const style =
				currentDisplayedStyles.find((item) => item.styleId === currentStyle.styleId) ??
				currentStyle
			return (
				<div
					className="flex flex-col text-sm font-normal text-base-100"
					style={{ lineHeight: "23px" }}
				>
					<p>{t("styleDetail.usingReferences", { count: style.referenceCount ?? 0 })}</p>
					{style.createdAt && (
						<p>
							{t("styleDetail.createdOn", {
								date: new Date(style.createdAt).toLocaleDateString(undefined, {
									month: "short",
									day: "numeric",
									year: "numeric",
								}),
							})}
						</p>
					)}
				</div>
			)
		}
	}, [isPolarrStyle, description, currentStyle, currentDisplayedStyles])

	const onStyleNameChanged = (styleId: string, name: string) => {
		const newStyles = currentDisplayedStyles.map((item) =>
			item.styleId === styleId ? { ...item, name } : item,
		)
		setCurrentDisplayedStyles(newStyles)
	}

	const onStyleCoverChanged = (styleId: string, coverImageUrl: string) => {
		const newStyles = currentDisplayedStyles.map((item) =>
			item.styleId === styleId ? { ...item, coverImageUrl } : item,
		)
		setCurrentDisplayedStyles(newStyles)
	}

	const onReferenceDeleted = (styleId: string, referenceCount: number) => {
		const newStyles = currentDisplayedStyles.map((item) =>
			item.styleId === styleId ? { ...item, referenceCount } : item,
		)
		setCurrentDisplayedStyles(newStyles)
	}

	const onStyleDeleted = () => {
		onBack()
	}

	const isCurrentStyle = isFullWidth
		? false
		: currentAppliedStyle?.styleId === currentStyle.styleId
			? true
			: false

	return (
		<div
			ref={containerRef}
			className={`flex size-full flex-col overflow-y-auto overflow-x-hidden ${LAYOUT_CONFIG.containerPadding} pb-8`}
		>
			{header}

			<div className="flex flex-1 flex-col">
				<div className="mb-8 flex flex-col gap-8 md:flex-row md:items-start">
					<div className="flex flex-1 flex-col space-y-6">
						<div className="space-y-4">
							<h2 className="text-xl font-bold transition-all duration-300 ease-in-out">
								{title}
							</h2>
							<div className="flex items-center space-x-6 text-sm text-black dark:text-white">
								<div className="flex items-center space-x-2">
									{authorProfile}
									<span>{t("styleDetail.byAuthor", { author })}</span>
								</div>
							</div>
						</div>

						{styleDescription}

						<div className="flex w-60 flex-row items-center space-x-2">
							<Button
								ref={useButtonRef}
								onClick={() => {
									onUse(currentStyle)
								}}
								trackingValue={title}
								trackingLabel="style_detail_use_ai_style"
								aria-label={
									isLoadingUse
										? t("styleDetail.ariaLoadingAiStyle")
										: isCurrentStyle
											? t("styleDetail.ariaAiStyleInUse")
											: t("styleDetail.ariaUseAiStyle")
								}
								variant={isCurrentStyle ? "default" : "primary"}
								className="h-9 flex-1 transition-all duration-300"
								disabled={isCurrentStyle || isLoadingUse}
							>
								{isLoadingUse
									? t("styleDetail.loadingAiStyle")
									: isCurrentStyle
										? t("styleDetail.inUse")
										: t("styleDetail.useAiStyle")}
							</Button>
							{!isPolarrStyle && isFullWidth && (
								<UserStyleContextMenu
									userStyle={displayedStyle ?? currentStyle}
									onStyleDeleted={onStyleDeleted}
									onStyleRenamed={onStyleNameChanged}
									onReferenceDeleted={onReferenceDeleted}
									onStyleDuplicated={onBack}
									onStyleCoverChanged={onStyleCoverChanged}
								/>
							)}
						</div>
					</div>

					<div className="w-full shrink-0 md:w-1/2">
						<StyleCover
							url={
								coverPhoto
									? processImageUrl({
											url: coverPhoto,
											format: ImageFormat.AVIF,
										})
									: undefined
							}
							title={title}
						/>
					</div>
				</div>

				{sampleBeforeAfterPhotos.length > 0 && (
					<div className="flex flex-1">{previewPanel()}</div>
				)}
			</div>
			{compareData && (
				<CompareDialog
					beforeAfterPhotos={compareData.photos}
					initialIndex={compareData.initialIndex}
					onNavigate={() => {}}
					onClose={() => setCompareData(null)}
				/>
			)}
		</div>
	)
}
